import React, { useState, useEffect, useCallback, useRef } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Bar, LinePath } from '@visx/shape';
import { Group } from '@visx/group';
import { AxisBottom } from '@visx/axis';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
} from '@mui/material';
import PropTypes from 'prop-types';

import {
  getParams,
  getMonth,
  thousandFormat2,
} from '../../../../Utils/Functions';
import {
  negativeActiveColor,
  negativeDisableColor,
  barHeight,
  ship,
  axis,
} from '../../../../Assets/js/constant';

const y = (d, key) => d[key];
const ex = (d) => d.WorkGeneratedAmount.Existing;
const ne = (d) => d.WorkGeneratedAmount.New;

const MiddleChart = ({
  queryData,
  filterName,
  selectedYears,
  selectedMonths,
  selectedTopItems,
  selectedMiddleItems,
  handleFilter,
}) => {
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const divElementRef = useRef(null);

  const [resize, setResize] = useState(false);
  const [data, setData] = useState([]);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [selectedRects, setSelectedRects] = useState([]);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    prepareData();
  }, [queryData, selectedYears, selectedMonths, selectedTopItems, filterName]);

  const prepareData = useCallback(() => {
    let dictData = {};
    let newData = [];

    let _selectedMonths = selectedMonths.slice();
    let _selectedyears = [];
    let _selectedTypes = [];
    
    selectedTopItems.forEach((item) => {
      _selectedMonths.push(item['month']);
      if (item['year']) {
        _selectedyears.push(item['year']);
      }

      if (item['type']) {
        _selectedTypes.push(item['type']);
      }
    });

    if (_selectedyears.length === 0) _selectedyears = selectedYears;

    if (_selectedTypes.length === 0) {
      _selectedTypes = ['Existing', 'New'];
    }

    if (queryData['MiddleLeft']) {
      queryData['MiddleLeft'].forEach((d) => {
        if (
          _selectedMonths.length === 0 ||
          ((_selectedMonths.indexOf(getMonth(d.Date)) > -1 ||
            _selectedMonths[0] === undefined) &&
            _selectedyears.indexOf(d.FY) > -1 &&
            _selectedTypes.indexOf(d.ExistingOrNew) > -1)
        ) {
          let item = d[filterName];

          if (!dictData[item]) {
            dictData[item] = {};
            dictData[item][filterName] = item;
            dictData[item]['WorkGeneratedAmount'] = [];
            dictData[item]['WorkGeneratedAmount']['Existing'] = 0;
            dictData[item]['WorkGeneratedAmount']['New'] = 0;
          }
          dictData[item]['WorkGeneratedAmount'][d.ExistingOrNew] += parseFloat(
            d['WorkGeneratedAmount'],
          );
        }
      });
    }

    Object.keys(dictData).map((key) => newData.push(dictData[key]));

    setData(newData);
  }, [queryData, filterName, selectedYears, selectedMonths, selectedTopItems]);

  const getColor = useCallback((data, type = '') => {
    const activeColor = type === 'Existing' ? negativeActiveColor : '#1c75bc';
    const disableColor = type === 'New' ? negativeDisableColor : '#ccdce9';

    if (selectedMiddleItems.length === 0) return activeColor;
    if (selectedMiddleItems.indexOf(data[filterName]) > -1) return activeColor;
    return disableColor;
  }, [filterName, selectedMiddleItems]);

  const handleFilterChange = useCallback((event) => {
    const newFilterName = event.target.value;
    handleFilter({ filterName: newFilterName });
  }, [handleFilter]);

  const handleElement = useCallback((event, element, item = null) => {
    let _selectedBars = [],
      _selectedRects = [],
      _selectedMiddleItems = [];
    let exist = false,
      index = NaN;

    switch (element) {
      case 'bar':
        if (event.shiftKey) {
          _selectedBars = selectedBars.slice();
          _selectedMiddleItems = selectedMiddleItems.slice();
          _selectedRects = selectedRects.slice();

          for (let i = 0; i < selectedBars.length; i++) {
            if (selectedBars[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('barActive');
            _selectedBars.push(event.target);
            _selectedMiddleItems.push(item);
          } else {
            event.target.classList.remove('barActive');
            _selectedBars.splice(index, 1);
            _selectedMiddleItems.splice(index, 1);
          }
        } else {
          selectedBars.forEach((selectedBar) => {
            selectedBar.classList.remove('barActive');
            if (selectedBar === event.target) exist = true;
          });

          selectedRects.forEach((selectedRect) => {
            selectedRect.classList.remove('bkgActive');
          });

          if (exist && selectedBars.length === 1) {
            _selectedBars = [];
            _selectedRects = [];
            _selectedMiddleItems = [];
          } else {
            event.target.classList.add('barActive');
            _selectedBars = [event.target];
            _selectedRects = [];
            _selectedMiddleItems = [item];
          }
        }

        break;

      case 'rect':
        if (event.shiftKey) {
          _selectedBars = selectedBars.slice();
          _selectedMiddleItems = selectedMiddleItems.slice();
          _selectedRects = selectedRects.slice();

          for (let i = 0; i < selectedRects.length; i++) {
            if (selectedRects[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('bkgActive');
            _selectedRects.push(event.target);
            _selectedMiddleItems.push(item);
          } else {
            event.target.classList.remove('bkgActive');
            _selectedRects.splice(index, 1);
            _selectedMiddleItems.splice(index, 1);
          }
        } else {
          selectedBars.forEach((selectedBar) => {
            selectedBar.classList.remove('barActive');
          });
          selectedRects.forEach((selectedRect) => {
            selectedRect.classList.remove('bkgActive');
            if (selectedRect === event.target) exist = true;
          });

          if (exist && selectedRects.length === 1) {
            _selectedBars = [];
            _selectedRects = [];
            _selectedMiddleItems = [];
          } else {
            event.target.classList.add('bkgActive');
            _selectedBars = [];
            _selectedRects = [event.target];
            _selectedMiddleItems = [item];
          }
        }

        break;

      default:
        break;
    }

    setSelectedBars(_selectedBars);
    setSelectedRects(_selectedRects);

    handleFilter({ selectedMiddleItems: _selectedMiddleItems });
  }, [selectedBars, selectedRects, selectedMiddleItems, handleFilter]);

  const deSelectAll = useCallback(() => {
    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    selectedRects.forEach((selectedRect) => {
      selectedRect.classList.remove('bkgActive');
    });
    
    localStorage.removeItem('wgSelectedMiddleItems');
    setSelectedBars([]);
    setSelectedRects([]);

    handleFilter({ selectedMiddleItems: [] });
  }, [selectedBars, selectedRects, handleFilter]);

  const showTooltipHandler = useCallback((event, data, type, isBar = true) => {
    let tooltipData, top, left;

    top = event.nativeEvent.layerY - 20;
    left = event.nativeEvent.layerX;
    tooltipData = data;
    tooltipData['isBar'] = isBar;
    tooltipData['type'] = type;
    
    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
    }

    showTooltip({
      tooltipData: tooltipData,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');
    setHoverBar(null);

    hideTooltip();
  }, [hoverBar, hideTooltip]);

  // Sort data
  const sortedData = data.sort(
    (a, b) =>
      a.WorkGeneratedAmount.Existing +
      a.WorkGeneratedAmount.New -
      (b.WorkGeneratedAmount.Existing + b.WorkGeneratedAmount.New),
  );

  const count = sortedData.length;
  let nameLength = 6;

  sortedData.forEach((d) => {
    nameLength = Math.max(nameLength, d[filterName]?.length || 0);
  });

  let width = (window.innerWidth - 16) / 2 - 8 - nameLength * 8;
  const height = count * 38 + 3;

  const margin = {
    top: 0,
    right: 10,
    bottom: 0,
    left: 0,
  };

  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );

  let maxValue = 0;
  let minValue = 0;
  if (count > 0) {
    let a, b;
    sortedData.forEach((d) => {
      a = ex(d);
      b = ne(d);
      if (a >= 0 && b >= 0) {
        maxValue = Math.max(maxValue, a + b);
        minValue = Math.min(minValue, a, b);
      } else if (a >= 0 && b < 0) {
        maxValue = Math.max(maxValue, a);
        minValue = Math.min(minValue, b);
      } else if (a < 0 && b >= 0) {
        maxValue = Math.max(maxValue, b);
        minValue = Math.min(minValue, a, b);
      } else {
        maxValue = Math.max(maxValue, b);
        minValue = Math.min(minValue, a);
      }
    });
  }

  // scales
  const xScale = scaleLinear({
    domain: [minValue, maxValue],
    range: [0, xMax],
    nice: true,
  });

  const yScale = scaleBand()
    .domain(sortedData.map((d) => y(d, filterName)))
    .rangeRound([yMax, 0])
    .padding(0.2);

  return (
    <div ref={divElementRef}>
      <div className="wrapper">
        <FormControlLabel
          control={
            <FormControl variant="standard">
              <Select
                value={filterName}
                name="filterName"
                onChange={handleFilterChange}
                sx={{
                  '& .MuiInputBase-input': {
                    padding: 0,
                    paddingRight: 24,
                    fontWeight: 'bold',
                    fontFamily: 'Verdana !important',
                    fontSize: '10pt',
                  },
                }}
              >
                <MenuItem value="EmployeeName">Project Source 1</MenuItem>
                <MenuItem value="ProjectSourceTwo">Project Source 2</MenuItem>
                <MenuItem value="ProjectTypeDescription">
                  Project Type
                </MenuItem>
              </Select>
            </FormControl>
          }
          label="By&nbsp;"
          labelPlacement="start"
          sx={{
            marginLeft: '0px !important',
            '& .MuiTypography-root': {
              fontWeight: 'bold',
              fontFamily: 'Verdana !important',
              fontSize: '10pt',
            },
          }}
        />
      </div>

      {count > 0 ? (
        <>
          <Box
            display="flex"
            style={{
              ...ship(yMax > 180 ? 190 : yMax + 10),
              border: 'solid 1px #a9a9a9',
            }}
          >
            <div style={{ width: `${nameLength * 8}px`, paddingLeft: 4, paddingTop: 3 }}>
              {sortedData
                .slice()
                .reverse()
                .map((d, i) => {
                  return (
                    <p
                      key={i}
                      style={{
                        height: 36,
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      className="grayHover"
                      onClick={(event) =>
                        handleElement(event, 'rect', d[filterName])
                      }
                    >
                      {d[filterName]}
                    </p>
                  );
                })}
            </div>

            <div style={{ width: `${width}px` }}>
              <svg width={width} height={height}>
                <rect
                  width={width > 0 ? width : 0}
                  height={height > 0 ? height : 0}
                  fill="white"
                  onClick={deSelectAll}
                />
                <Group top={margin.top} left={margin.left}>
                  {sortedData.map((d, i) => {
                    let a = 0,
                      b = 0;
                    let x0 = xScale(0),
                      x1 = xScale(0),
                      w0 = 0,
                      w1 = 0;
                    a = ex(d); // Existing value
                    b = ne(d); // New value
                    if (a >= 0 && b >= 0) {
                      x0 = xScale(0);
                      w0 = xScale(b) - x0;
                      x1 = x0 + w0;
                      w1 = xScale(a) - xScale(0);
                    } else if (a >= 0 && b < 0) {
                      x0 = xScale(0) - xScale(b);
                      w0 = xScale(0) - x0;
                      x1 = xScale(0);
                      w1 = xScale(a) - x1;
                    } else if (a < 0 && b >= 0) {
                      x0 = xScale(0);
                      w0 = xScale(b) - x0;
                      x1 = xScale(0) - xScale(a);
                      w1 = xScale(0) - x1;
                    } else {
                      x0 = xScale(0) - xScale(b);
                      w0 = xScale(0) - x0;
                      x1 = x0 - xScale(a);
                      w1 = x0 - x1;
                    }

                    const barHeight = yScale.bandwidth() - 2;
                    const barY = yScale(y(d, filterName));

                    return (
                      <Group key={`${filterName}-${y(d, filterName)}-${i}`}>
                        <Bar
                          x={x0}
                          y={barY}
                          width={w0 > 0 ? w0 : 0}
                          height={barHeight > 0 ? barHeight : 0}
                          fill={getColor(d, 'New')}
                          onClick={(event) => {
                            handleElement(event, 'bar', d[filterName]);
                          }}
                          onMouseMove={(event) => {
                            showTooltipHandler(event, d, 'New');
                          }}
                          onMouseLeave={hideTooltipHandler}
                          onTouchMove={(event) => {
                            showTooltipHandler(event, d, 'New');
                          }}
                          onTouchEnd={hideTooltipHandler}
                        />
                        <Bar
                          x={x1}
                          y={barY}
                          width={w1 > 0 ? w1 : 0}
                          height={barHeight > 0 ? barHeight : 0}
                          fill={getColor(d, 'Existing')}
                          onClick={(event) => {
                            handleElement(event, 'bar', d[filterName]);
                          }}
                          onMouseMove={(event) => {
                            showTooltipHandler(event, d, 'Existing');
                          }}
                          onMouseLeave={hideTooltipHandler}
                          onTouchMove={(event) => {
                            showTooltipHandler(event, d, 'Existing');
                          }}
                          onTouchEnd={hideTooltipHandler}
                        />

                        <LinePath
                          data={[
                            { x: xScale(0), y: 0 },
                            { x: xScale(0), y: yMax },
                          ]}
                          x={(d) => (d.x ? d.x : 0)}
                          y={(d) => (d.y ? d.y : 0)}
                          stroke="#555555"
                          strokeDasharray="2,1"
                        />
                        <LinePath
                          data={[
                            { x: 0, y: 0 },
                            { x: 0, y: yMax },
                          ]}
                          x={(d) => (d.x ? d.x : 0)}
                          y={(d) => (d.y ? d.y : 0)}
                          stroke="#555555"
                        />
                      </Group>
                    );
                  })}
                </Group>
              </svg>

              {tooltipOpen && (
                <TooltipWithBounds
                 
                  top={tooltipTop + pageYOffset}
                  left={tooltipLeft}
                >
                  {tooltipData.isBar ? (
                    <div className="bkg-white color-black">
                      <div className="pdv-5">
                        <strong>
                          {tooltipData[filterName]} - {tooltipData['type']}
                        </strong>
                      </div>
                      <div className="ft-12">
                        {' '}
                        {thousandFormat2(
                          tooltipData['WorkGeneratedAmount'][
                            tooltipData['type']
                          ],
                        )}
                      </div>
                    </div>
                  ) : null}
                </TooltipWithBounds>
              )}
            </div>
          </Box>

          <Grid container style={axis}>
            <div style={{ width: `${nameLength * 8}px` }}></div>

            <div style={{ width: `${width}px` }}>
              <svg
                width={width + 30}
                height={barHeight}
                style={{ marginLeft: -8 }}
              >
                <rect
                  x={0}
                  y={0}
                  width={width + 30 > 0 ? width + 30 : 0}
                  height={barHeight > 0 ? barHeight : 0}
                  fill={'transparent'}
                  onClick={(event) => {
                    deSelectAll();
                  }}
                />
                <AxisBottom
                  scale={xScale}
                  left={10}
                  top={0}
                  hideAxisLine={true}
                  stroke="black"
                  tickStroke="black"
                  tickFormat={(value, index) => value / 1000}
                  tickLabelProps={(value, index) => ({
                    fill: 'black',
                    fontSize: 11,
                    textAnchor: 'middle',
                    dy: '0.2em',
                  })}
                  tickComponent={({ formattedValue, ...tickProps }) => (
                    <text {...tickProps} onClick={deSelectAll}>
                      $
                      {Math.abs(formattedValue) < 1000
                        ? `${formattedValue}K`
                        : `${formattedValue / 1000}M`}
                    </text>
                  )}
                />
              </svg>
            </div>
          </Grid>
        </>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 180 }}>
          No data available
        </Box>
      )}
    </div>
  );
};

MiddleChart.propTypes = {
  queryData: PropTypes.object.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedMonths: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedMiddleItems: PropTypes.array.isRequired,
  filterName: PropTypes.string.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default MiddleChart;
